import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { translateSignal } from '@jsverse/transloco';
import { ModalController, Platform } from '@ionic/angular/standalone';
import { SoloTierData } from 'src/app/core/api/player/schemas/solo-tiers';
import {
  getModalConfig,
  modalContent,
  shouldShowAsSheetModal,
} from 'src/app/core/services/modal/modal.utils';
import { AnalyticsService } from 'src/app/core/services/analytics/analytics.service';
import { EventName } from 'src/app/core/services/analytics/event-names';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { SoloTiersService } from 'src/app/core/services/tiers/solo-tiers.service';
import {
  SOLO_QUOTE_REFRESH_LEAD_MS,
  SoloQuoteService,
} from 'src/app/core/services/tiers/solo-quote.service';
import { PaymentMethodType } from 'src/app/features/payments/services/models/payment-method-type';
import {
  SoloLightningPayment,
  SoloOperatorPayment,
  SoloPayOutcome,
  SoloSheetPayment,
} from 'src/app/features/payments/services/solo-sheet-payment';
import { SoloSheetViewComponent } from '../components/solo-sheet-view/solo-sheet-view.component';
import { CurrencyAmountDisplay } from '../../../shared/utils/currency/currency-format';
import { MULTIPLIER_MARKER, splitAtMultiplier } from '../utils/multiplier-sentence.utils';
import {
  SoloAmountDisplay,
  soloAmountDisplay,
  sortSoloTiersByEntryFee,
} from '../utils/solo-tiers.utils';
import { SoloRulesDrawerComponent } from '../solo-rules-drawer/solo-rules-drawer.component';
import { SoloNewTargetDrawerComponent } from '../solo-new-target-drawer/solo-new-target-drawer.component';

/**
 * Where a Solo stake is chosen and paid for: the target to beat, a slider over the stakes wager
 * offers, the prize for the chosen stake, and Play, which pays with `payment` straight away. There
 * is no pay drawer after it. The payment closes the sheet once the game launches or the charge is
 * refused, except when wager refuses it before charging: the new-target drawer then opens over the
 * sheet, either to pay again for the same stake at the new target, or to say that stake is no
 * longer offered. On Lightning it opens when wager refuses a paid invoice for a moved target, to
 * continue that entry at the new target with no new payment. Closing that drawer closes the sheet
 * too.
 *
 * The target is wager's quote from SoloQuoteService, which the opener keeps live while the sheet
 * is open, under a bar counting down to its refetch; until the first quote lands it is the tiers'
 * target, the card's. The tiers are read live from SoloTiersService, which the opener refreshes as
 * the sheet opens. The slider shows exactly the tiers wager returns and is rendered even for one
 * tier.
 *
 * The sheet's look is solo-sheet-view, bound here from plain values; this container owns the
 * services, the payment and the modals a tap opens.
 */
@Component({
  selector: 'app-solo-sheet',
  templateUrl: './solo-sheet.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [SoloSheetViewComponent],
})
export class SoloSheetComponent implements OnDestroy {
  // Built by PaymentService.openSoloSheet for this sheet's payment intent.
  readonly payment = input.required<SoloSheetPayment>();

  private readonly soloTiersService = inject(SoloTiersService);
  private readonly soloQuoteService = inject(SoloQuoteService);
  private readonly themeService = inject(ThemeService);
  private readonly modalController = inject(ModalController);
  private readonly platform = inject(Platform);
  private readonly analyticsService = inject(AnalyticsService);

  // The Rules sheet this sheet opened, while it is open. The payment closes this sheet by
  // identity, not the top modal, so a launch or an emptied stake list would otherwise leave the
  // Rules sheet over the game.
  private rulesModal: HTMLIonModalElement | undefined;
  private openingRules = false;
  // Set once this sheet starts closing, so Rules built after that are never shown.
  private closing = false;
  // The new-target drawer, while it is open, closed with this sheet for the same reason.
  private newTargetModal: HTMLIonModalElement | undefined;
  // Set from the refusal that opens the new-target drawer until that drawer has closed, including
  // while it is being built.
  private readonly newTargetOpen = signal(false);
  private destroyed = false;

  // The device clock, read four times a second: the countdown's seconds turn on time, and the
  // bar's width moves in steps small enough for its CSS transition to smooth.
  private readonly now = signal(Date.now());
  private readonly ticker = setInterval(() => this.now.set(Date.now()), 250);

  readonly tiers = computed(() => sortSoloTiersByEntryFee(this.soloTiersService.soloTiers()));

  // Kept by id rather than slider position, so a refresh that adds or drops a stake below the
  // chosen one leaves the player on the stake they picked.
  private readonly chosenTierId = signal<string | undefined>(undefined);

  // The sheet opens on the highest stake, every time it opens, and a chosen stake that is no
  // longer offered falls back to the highest too.
  readonly selectedIndex = computed(() => {
    const chosen = this.tiers().findIndex((tier) => tier.tierId === this.chosenTierId());
    return chosen === -1 ? Math.max(this.lastIndex(), 0) : chosen;
  });
  readonly selectedTier = computed(
    (): SoloTierData | undefined => this.tiers()[this.selectedIndex()],
  );
  readonly lastIndex = computed(() => this.tiers().length - 1);
  // A lone stake is also the highest, so the slider shows it at the right end on a full track. A
  // range input with max 0 would put its thumb at the left, so a lone stake gets a 0..1 range
  // pinned at 1, and the input is disabled so it can't be dragged off that end.
  readonly singleStake = computed(() => this.lastIndex() === 0);
  readonly sliderMax = computed(() => (this.singleStake() ? 1 : this.lastIndex()));
  readonly sliderValue = computed(() => (this.singleStake() ? 1 : this.selectedIndex()));
  // The filled share of the track, for the progress fill drawn behind the thumb.
  readonly progressPercent = computed(() => (this.sliderValue() / this.sliderMax()) * 100);

  private readonly quote = this.soloQuoteService.quote;
  readonly quoteFailed = this.soloQuoteService.failed;
  // Until the first quote lands, the tiers' target: the number the card showed.
  readonly target = computed(() => {
    const targetScore = this.quote()?.targetScore ?? this.selectedTier()?.targetScore;
    return targetScore === undefined ? '' : this.themeService.formatNumber(targetScore);
  });
  private readonly quoteRemainingMs = computed(() => {
    const quote = this.quote();
    if (!quote) {
      return 0;
    }
    return Math.min(Math.max(quote.lockedUntil - this.now(), 0), quote.validityMs);
  });
  readonly quoteSeconds = computed(() => Math.ceil(this.quoteRemainingMs() / 1000));
  // The bar's denominator is the held quote's own validity. With no quote, or a zero validity,
  // there is nothing to divide by and the bar is empty.
  readonly quotePercent = computed(() => {
    const validityMs = this.quote()?.validityMs;
    return validityMs ? (this.quoteRemainingMs() / validityMs) * 100 : 0;
  });
  // Play pays at the held quote only while it has more than the refetch lead left. The refetch
  // turns Play off from that moment anyway; this also covers a timer that fires late.
  private readonly quoteReady = computed(
    () => this.soloQuoteService.ready() && this.quoteRemainingMs() > SOLO_QUOTE_REFRESH_LEAD_MS,
  );
  // Every tier pays the same multiple, so the selected one's is the sheet's.
  private readonly multiplier = computed(() => {
    const multiplier = this.selectedTier()?.multiplier;
    return multiplier === undefined ? '' : this.themeService.formatNumber(Number(multiplier));
  });
  // A signal, so the rule follows a language switch as the template's other text does.
  private readonly ruleSentence = translateSignal('components.solo.rule', {
    multiplier: MULTIPLIER_MARKER,
  });
  // The rule line, cut around the multiplier so the template colours "7x" alone, as the card's
  // call to action does.
  readonly rule = computed(() => splitAtMultiplier(this.ruleSentence(), this.multiplier()));
  readonly entry = computed(() => this.format(this.selectedTier(), 'entryFee'));
  readonly prize = computed(() => this.format(this.selectedTier(), 'prize'));
  // One label per stake, centred under the thumb's position for that stake: `position` is the
  // share of the thumb's travel, matching progressPercent, so a lone stake's label sits at the
  // end where the slider pins it. The primary figure only, as a stop label has no room for the
  // secondary currency. Kept as the whole display, not its text, because an icon currency
  // carries its symbol in the icon.
  readonly stops = computed((): { amount: CurrencyAmountDisplay; position: number }[] =>
    this.tiers().map((tier, index) => ({
      amount: this.display(tier, 'entryFee').primary,
      position: this.singleStake() ? 100 : (index / this.sliderMax()) * 100,
    })),
  );

  readonly lightning = computed((): SoloLightningPayment | undefined => {
    const payment = this.payment();
    return payment.kind === 'lightning' ? payment : undefined;
  });
  readonly showInvoiceActions = computed(
    () => this.lightning()?.method === PaymentMethodType.LightningWeb,
  );
  // Desktop web scans the QR from another device. The view sets it in the stake panel beside Copy,
  // so the code reads as the chosen stake's and the sheet keeps room below it; mobile web has only
  // Copy, which stays above Pay as on the pay drawer.
  readonly showQR = computed(() => this.lightning()?.showQR ?? false);
  // The selected stake's invoice. Undefined until its mint lands, while the QR and Pay wait.
  private readonly selectedDeposit = computed(() => {
    const tierId = this.selectedTier()?.tierId;
    return tierId ? this.lightning()?.invoices().get(tierId) : undefined;
  });
  readonly selectedInvoice = computed(() => this.selectedDeposit()?.invoice);
  // Set once a native or operator wallet has the invoice: the sheet shows the waiting state.
  readonly waiting = computed(() => this.lightning()?.waiting());
  readonly invoiceCancelled = computed(() => this.lightning()?.invoiceCancelled() ?? false);

  // Set from the Play tap: the stake can no longer change once it is being paid for.
  readonly paying = computed(() => {
    const payment = this.payment();
    return payment.kind === 'operator' ? payment.busy() : false;
  });

  // Off while the new-target drawer is up or being built: only the drawer may pay again, and only
  // for the refused stake (SoloOperatorPayment).
  readonly playDisabled = computed(
    () => this.paying() || this.newTargetOpen() || !this.selectedTier() || !this.quoteReady(),
  );
  // Pay spins until the selected stake's invoice is minted.
  readonly invoicePending = computed(() => !this.selectedInvoice());
  // Off under the new-target drawer too: one paid invoice is already waiting on it.
  readonly payDisabled = computed(() => this.invoicePending() || this.newTargetOpen());
  readonly hasSecondaryCurrency = computed(() => this.themeService.hasSecondaryCurrency);

  // Rules is offered while a stake is being chosen, not once it is being paid for: a charge that
  // succeeds launches the game, and Rules opened during it would sit over the launch.
  readonly showRules = computed(() => !this.waiting() && !this.paying());

  constructor() {
    // A QR on screen can be scanned, so its invoice must keep being checked after the sheet
    // closes. The payment drops every invoice nobody saw.
    effect(() => {
      const lightning = this.lightning();
      const deposit = this.selectedDeposit();
      if (lightning?.showQR && deposit) {
        untracked(() => lightning.expose(deposit.id));
      }
    });

    // A paid invoice wager refused for a moved target. Its stake may not be the slider's, or may
    // no longer be offered: the deposit's amount is fixed, and wager decides whether it still
    // enters.
    effect(() => {
      const lightning = this.lightning();
      const held = lightning?.heldEntry();
      if (!lightning || !held) {
        return;
      }
      untracked(() => {
        const tier = this.tiers().find((candidate) => candidate.tierId === held.tierId);
        void this.showNewTarget({
          tierId: held.tierId,
          entry: tier ? this.display(tier, 'entryFee') : undefined,
          pay: () => lightning.continueEntry(),
          tierRefused: false,
          paid: true,
        });
      });
    });

    // The refresh on open can find Solo switched off, or every stake gone. Nothing is left to
    // choose, and the card behind the sheet has already hidden, so the sheet closes too. Not
    // while a payment is under way: a failed refetch empties the list as well, and closing would
    // stop the sheet launching the game that a running charge or a handed-over invoice buys. The
    // payment closes the sheet itself when it ends. Nor under the new-target drawer, which tells
    // the player their stake has gone and closes the sheet when they dismiss it.
    effect(() => {
      if (this.tiers().length === 0 && !this.paying() && !this.waiting() && !this.newTargetOpen()) {
        untracked(() => void this.payment().dismissSheet());
      }
    });
  }

  onStakeSelected(index: number): void {
    this.choose(index);
  }

  onStep(delta: -1 | 1): void {
    this.choose(this.selectedIndex() + delta);
  }

  // Lightning's Pay: opens the wallet on web, hands the invoice over on native and operator
  // wallets. A paid invoice launches the game and closes the sheet from the payment.
  onPayClick(): void {
    const tier = this.selectedTier();
    const lightning = this.lightning();
    if (!tier || !lightning || this.payDisabled()) {
      return;
    }
    lightning.pay(tier.tierId);
  }

  async onCopyClick(): Promise<void> {
    const tierId = this.selectedTier()?.tierId;
    if (tierId) {
      await this.lightning()?.copy(tierId);
    }
  }

  async onPlayClick(): Promise<void> {
    const payment = this.payment();
    const tier = this.selectedTier();
    if (this.playDisabled() || payment.kind !== 'operator' || !tier) {
      return;
    }
    const outcome = await this.charge(payment, tier);
    if (outcome === 'target-changed' || outcome === 'tier-unavailable') {
      await this.showNewTarget({
        tierId: tier.tierId,
        entry: this.display(tier, 'entryFee'),
        pay: () => this.charge(payment, tier),
        tierRefused: outcome === 'tier-unavailable',
        paid: false,
      });
    }
  }

  async onCloseClick(): Promise<void> {
    await this.payment().dismissSheet();
  }

  async onRulesClick(): Promise<void> {
    // A second tap while the first is still building its sheet would otherwise open a second one.
    if (this.rulesModal || this.openingRules) {
      return;
    }
    this.openingRules = true;
    const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
    let modal: HTMLIonModalElement;
    try {
      modal = await this.modalController.create({
        component: SoloRulesDrawerComponent,
        ...getModalConfig(asSheetModal, { mobile: 'custom-modal-height' }),
      });
    } finally {
      this.openingRules = false;
    }
    // The sheet can close while the Rules sheet is being built: never present it over the game.
    if (this.closing) {
      modal.remove();
      return;
    }
    this.rulesModal = modal;
    void modal.onDidDismiss().then(() => {
      if (this.rulesModal === modal) {
        this.rulesModal = undefined;
      }
    });
    await modal.present();
  }

  // Ionic calls this as the sheet's modal starts its leave animation, so Rules closes together
  // with the sheet rather than staying over the launching game until the animation ends. A moved
  // target from then on is refunded with the player told, never asked about in a new-target drawer
  // over the leaving sheet.
  ionViewWillLeave(): void {
    this.closeRules();
    this.lightning()?.closeIntent();
  }

  // Also closes Rules for a sheet torn down without a leave animation.
  ngOnDestroy(): void {
    this.destroyed = true;
    clearInterval(this.ticker);
    this.closeRules();
    void this.newTargetModal?.dismiss();
  }

  private closeRules(): void {
    this.closing = true;
    void this.rulesModal?.dismiss();
  }

  // Charges `tier` at the held quote. A moved target leaves the sheet open, holding the fresh quote
  // wager sent with the refusal; a withdrawn stake refreshes the tiers so the slider drops it.
  private async charge(payment: SoloOperatorPayment, tier: SoloTierData): Promise<SoloPayOutcome> {
    const quote = this.quote();
    if (!quote) {
      return 'busy';
    }
    const outcome = await payment.pay(tier, quote.quoteId);
    if (outcome === 'tier-unavailable') {
      void this.soloTiersService.refresh();
    }
    if (outcome === 'target-changed') {
      this.analyticsService.log(EventName.SoloTargetChanged, {
        tierId: tier.tierId,
        oldTarget: quote.targetScore,
        newTarget: this.quote()?.targetScore,
      });
    }
    return outcome;
  }

  // Resolves once the drawer has closed. Its Pay charges the refused stake again, never the
  // slider's current one, so a re-pay under the intent's key is always for the stake its deposit
  // was written with (SoloOperatorPayment); on Lightning its Continue enters the paid deposit. A
  // second refusal keeps the drawer open rather than opening another. The drawer can only be
  // closed by the player or by this sheet closing, and a player closing it ends the sheet too.
  private async showNewTarget(drawer: {
    tierId: string;
    entry: SoloAmountDisplay | undefined;
    pay: () => Promise<SoloPayOutcome>;
    tierRefused: boolean;
    paid: boolean;
  }): Promise<void> {
    if (this.newTargetOpen() || this.destroyed) {
      return;
    }
    this.newTargetOpen.set(true);
    try {
      const asSheetModal = shouldShowAsSheetModal(this.platform, this.themeService);
      const modal = await this.modalController.create({
        ...modalContent(SoloNewTargetDrawerComponent, {
          ...drawer,
          hasSecondaryCurrency: this.hasSecondaryCurrency(),
        }),
        ...getModalConfig(asSheetModal),
      });
      // The sheet can close while the drawer is being built: never present it over the game.
      if (this.destroyed) {
        modal.remove();
        return;
      }
      this.newTargetModal = modal;
      await modal.present();
      await modal.onDidDismiss();
      this.newTargetModal = undefined;
    } finally {
      this.newTargetOpen.set(false);
    }
    if (!this.destroyed) {
      await this.payment().dismissSheet();
    }
  }

  private choose(index: number): void {
    const tier = this.tiers()[Math.min(Math.max(index, 0), this.lastIndex())];
    if (tier) {
      this.chosenTierId.set(tier.tierId);
    }
  }

  private format(
    tier: SoloTierData | undefined,
    field: 'entryFee' | 'prize',
  ): SoloAmountDisplay | undefined {
    return tier ? this.display(tier, field) : undefined;
  }

  private display(tier: SoloTierData, field: 'entryFee' | 'prize'): SoloAmountDisplay {
    return soloAmountDisplay(
      tier.amounts,
      field,
      this.themeService.displayCurrency(),
      (currency, amount) => this.themeService.formatCurrencyAmount(currency, amount),
    );
  }
}
